import { Component, EventEmitter, Input, OnChanges, OnInit, Output } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { ParamMap } from '@angular/router';
import { provideTranslocoScope } from '@ngneat/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngxs/store';
import { ChangePasswordModel, UpdatePasswordMode } from '@platform-frontend/account';
import { ActionFilter, CustomValidators, ExclusiveActionFilter } from '@platform-frontend/ui-tools';
import { SimpleRouterState, TypedChanges } from '@platform-frontend/util';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { ACCOUNT_UI_TRANSLOCO_SCOPE } from '../account-ui-transloco-scope';

interface UpdatePasswordForm {
  currentPassword: FormControl<string>;
  password: FormControl<string>;
  confirmPassword: FormControl<string>;
}

@UntilDestroy()
@Component({
  selector: 'yat-update-password-form',
  templateUrl: './update-password-form.component.html',
  styleUrls: ['./update-password-form.component.scss'],
  providers: [provideTranslocoScope(ACCOUNT_UI_TRANSLOCO_SCOPE)]
})
export class UpdatePasswordFormComponent implements OnInit, OnChanges {
  constructor(private store: Store) {}

  @Output()
  updatePasswordMethod: EventEmitter<ChangePasswordModel> = new EventEmitter();

  @Output()
  forgotPasswordMethod: EventEmitter<void> = new EventEmitter();

  @Output()
  cancelMethod: EventEmitter<void> = new EventEmitter();

  @Input()
  spinnerActionFilter: ActionFilter = new ExclusiveActionFilter();

  @Input()
  emailControl: FormControl<string>;

  @Input()
  isChangePassword: boolean = false;

  @Input()
  passwordUpdateMode: UpdatePasswordMode = 'resetPassword';

  queryParams$: Observable<ParamMap> = this.store.select(SimpleRouterState.queryParamMap);
  email$: Observable<string>;

  updatePasswordForm = new FormGroup<UpdatePasswordForm>({
    currentPassword: new FormControl(
      { value: '', disabled: true },
      { nonNullable: true, validators: [Validators.required, CustomValidators.blacklisted(['open sesame'])] }
    ),
    password: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, CustomValidators.blacklisted(['123', '12345678', 'open sesame', 'simsalabim']), CustomValidators.passwordSecurity]
    }),
    confirmPassword: new FormControl('', { nonNullable: true, validators: [control => this.confirmPasswordValidator(control)] })
  });

  ngOnInit(): void {
    this.updatePasswordForm.controls.password.valueChanges
      .pipe(
        tap(() => this.updatePasswordForm.controls.confirmPassword.setErrors(this.confirmPasswordValidator(this.updatePasswordForm.controls.confirmPassword))),
        untilDestroyed(this)
      )
      .subscribe();
  }

  ngOnChanges(changes: TypedChanges<UpdatePasswordFormComponent>): void {
    if (changes.isChangePassword) {
      if (this.isChangePassword) {
        this.updatePasswordForm.controls.currentPassword.enable();
      } else {
        this.updatePasswordForm.controls.currentPassword.disable();
      }
    }
  }

  private confirmPasswordValidator(control: AbstractControl): ValidationErrors | null {
    if (this.updatePasswordForm && this.updatePasswordForm.controls.password.valid && control.value !== this.updatePasswordForm.controls.password.value) {
      return { notMatching: true };
    }
    return null;
  }

  submitForm(value: ChangePasswordModel): void {
    this.updatePasswordMethod.next(value);
  }

  forgotPassword(): void {
    this.forgotPasswordMethod.emit();
  }

  cancel(): void {
    this.cancelMethod.emit();
  }
}
